import { useEffect, useState, useCallback, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { Modal } from '../components/Modal';
import { useConfirm } from '../components/ConfirmProvider';
import { useUserOrg } from '../lib/org';
import { useSession } from '../lib/auth';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  listTeamMembers,
  listPendingInvites,
  getSeatUsage,
  createInvitation,
  revokeInvite,
  removeMember,
  tierLabel,
  nextTier,
  errMsg,
  type TeamMember,
  type PendingInvite,
  type SeatUsage,
} from '../lib/team';

function roleBadge(role: TeamMember['role']): string {
  switch (role) {
    case 'owner':  return 'Owner';
    case 'admin':  return 'Admin';
    case 'member': return 'Member';
    case 'viewer': return 'Viewer';
  }
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

export function Team() {
  const { org } = useUserOrg();
  const { confirm, alert } = useConfirm();
  const { session } = useSession();
  const isOwner = !!org && org.role === 'owner';
  const canInvite = !!org && (org.role === 'owner' || org.role === 'admin');

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [usage, setUsage] = useState<SeatUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite modal
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [createdEmail, setCreatedEmail] = useState<string | null>(null);
  const [createdEmailSent, setCreatedEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!org) return;
    setLoading(true);
    setError(null);
    try {
      const [m, i, u] = await Promise.all([
        listTeamMembers(org.org_id),
        listPendingInvites(org.org_id),
        getSeatUsage(org.org_id),
      ]);
      setMembers(m);
      setInvites(i);
      setUsage(u);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [org]);

  useEffect(() => { void refresh(); }, [refresh]);

  const seatsUsed = (usage?.used ?? 0) + (usage?.pending ?? 0);
  const atCap = !!usage && seatsUsed >= usage.cap;
  const upgradeTier = usage ? nextTier(usage.tier) : null;

  function openInvite() {
    setInviteEmail('');
    setCreatedLink(null);
    setCreatedEmail(null);
    setCopied(false);
    setInviteError(null);
    setInviteOpen(true);
  }

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    if (!org) return;
    setSubmitting(true);
    setInviteError(null);
    try {
      const result = await createInvitation({ orgId: org.org_id, email: inviteEmail });
      setCreatedLink(result.url);
      setCreatedEmail(inviteEmail);
      setCreatedEmailSent(result.emailSent);
      await refresh();
    } catch (e) {
      const msg = errMsg(e);
      // Helpful hint when the migration hasn't been deployed yet.
      if (/create_invitation/i.test(msg) || /not find the function/i.test(msg)) {
        setInviteError(
          "The invite system isn't fully wired up yet on the server. Run migration-012.sql in Supabase SQL Editor to enable invitations.",
        );
      } else {
        setInviteError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function copyLink() {
    if (!createdLink) return;
    try {
      await navigator.clipboard.writeText(createdLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback noop
    }
  }

  async function onRevokeInvite(inv: PendingInvite) {
    if (!(await confirm({
      title: `Cancel invite to ${inv.email}?`,
      body: 'The link will stop working. You can send a new invite any time.',
      confirmLabel: 'Cancel invite',
      danger: true,
    }))) return;
    try {
      await revokeInvite(inv.id);
      await refresh();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  async function onRemoveMember(m: TeamMember) {
    if (m.role === 'owner') {
      await alert({
        title: "Can't remove the owner",
        body: 'Transfer ownership first, then remove.',
      });
      return;
    }
    const label = m.full_name || m.email || 'this teammate';
    if (!(await confirm({
      title: `Remove ${label}?`,
      body: "They'll lose access immediately, but their login still works.",
      confirmLabel: 'Remove',
      danger: true,
    }))) return;
    try {
      await removeMember(m.id);
      await refresh();
    } catch (e) {
      setError(errMsg(e));
    }
  }

  if (!org) {
    return (
      <AppShell title="Add users" back="/settings">
        <LoadingIndicator />
      </AppShell>
    );
  }

  return (
    <AppShell title="Add users" back="/settings">
      {error && <div className="banner banner-error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Seat usage card */}
      <div className="card" style={{ marginBottom: 12 }}>
        {usage ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <p style={{ margin: 0, fontWeight: 600 }}>
                {seatsUsed} of {usage.cap} {usage.cap === 1 ? 'seat' : 'seats'} used
              </p>
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>{tierLabel(usage.tier)}</p>
            </div>
            <div
              style={{
                marginTop: 8,
                height: 6,
                background: 'var(--primary-pale, #E2E8F0)',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(100, (seatsUsed / usage.cap) * 100)}%`,
                  background: atCap ? 'var(--danger)' : 'var(--success)',
                  transition: 'width 200ms ease',
                }}
              />
            </div>
            <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
              {usage.used} active · {usage.pending} pending
              {usage.pending > 0 && ' (counted toward your cap until accepted or expired)'}
            </p>
          </>
        ) : (
          <LoadingIndicator />
        )}
      </div>

      {/* Invite button + upgrade CTA */}
      {canInvite && (
        <div style={{ marginBottom: 16 }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={atCap}
            onClick={openInvite}
            style={{ width: '100%' }}
          >
            + Invite teammate
          </button>
          {atCap && upgradeTier && (
            <p className="muted" style={{ fontSize: 13, marginTop: 8, textAlign: 'center' }}>
              You've used all your seats.{' '}
              {upgradeTier === 'enterprise' ? (
                <>Need more than 25 phones? <a href="mailto:hello@safeandsorted.com?subject=Enterprise%20seats">Get in touch for Enterprise</a>.</>
              ) : (
                <>
                  Need more? <a href={`mailto:hello@safeandsorted.com?subject=Upgrade%20to%20${encodeURIComponent(tierLabel(upgradeTier))}`}>Email us to upgrade to <strong>{tierLabel(upgradeTier)}</strong></a>.
                </>
              )}
            </p>
          )}
        </div>
      )}

      {/* Members */}
      <h3 className="section-title">Active</h3>
      {loading ? (
        <LoadingIndicator />
      ) : members.length === 0 ? (
        <p className="muted">No members yet — that's odd, you should be in here.</p>
      ) : (
        <ul className="person-list">
          {members.map((m) => {
            const isMe = session?.user.id === m.user_id;
            return (
              <li key={m.id}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 600 }}>
                      {m.full_name || m.email || 'Unnamed user'}
                      {isMe && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ink-soft)' }}>(you)</span>}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-soft)' }}>
                      {m.email ?? '—'} · {roleBadge(m.role)} · joined {fmtDate(m.joined_at)}
                    </p>
                  </div>
                  {isOwner && m.role !== 'owner' && !isMe && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => onRemoveMember(m)}
                      style={{ color: 'var(--danger)', borderColor: 'var(--danger)', padding: '6px 10px', fontSize: 12 }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Pending invites */}
      {invites.length > 0 && (
        <>
          <h3 className="section-title" style={{ marginTop: 24 }}>Pending invites</h3>
          <ul className="person-list">
            {invites.map((inv) => {
              const url = `${window.location.origin}/invite/${inv.token}`;
              return (
                <li key={inv.id}>
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>{inv.email}</p>
                      <span style={{ fontSize: 11, color: 'var(--ink-soft)' }}>
                        expires {fmtDate(inv.expires_at)}
                      </span>
                    </div>
                    <p style={{ margin: '4px 0', fontSize: 11, color: 'var(--ink-soft)', wordBreak: 'break-all' }}>
                      {url}
                    </p>
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={async () => {
                          try { await navigator.clipboard.writeText(url); } catch {}
                        }}
                      >
                        Copy link
                      </button>
                      {canInvite && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{ padding: '4px 10px', fontSize: 12, color: 'var(--danger)', borderColor: 'var(--danger)' }}
                          onClick={() => onRevokeInvite(inv)}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* Help text */}
      <div className="card" style={{ marginTop: 24, background: 'var(--bg-soft, #FAF7F2)' }}>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.6 }}>
          <strong>How it works:</strong> Tap <em>Invite teammate</em>, type their email, copy the link
          we generate and send it to them via WhatsApp, SMS or email. They tap it, set their own password
          and they're in. Each person needs their own login — shared logins fail an HSE audit because
          every signature traces to one account.{' '}
          {usage && (
            <>
              You're on <strong>{tierLabel(usage.tier)}</strong> — {usage.cap} active phones.
              Workers without an account sign in via the public QR code on the cabin door (free, unlimited).
            </>
          )}{' '}
          <Link to="/terms" className="muted" style={{ textDecoration: 'underline' }}>Read the terms</Link>.
        </p>
      </div>

      {/* Invite modal */}
      <Modal
        open={inviteOpen}
        title={createdLink ? 'Share this link with them' : 'Invite a teammate'}
        onClose={() => setInviteOpen(false)}
      >
        {!createdLink ? (
          <form onSubmit={onInvite}>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
              Enter their email — that's the address they'll need to sign in with. Each invite link
              expires after 7 days.
            </p>
            {inviteError && (
              <div className="banner banner-error" style={{ marginBottom: 12, fontSize: 13 }}>
                {inviteError}
              </div>
            )}
            <div className="field">
              <label htmlFor="inv-email">Email</label>
              <input
                id="inv-email"
                type="email"
                required
                autoComplete="email"
                inputMode="email"
                /* Tells iOS / Android to put a "Send" button where the
                   Return key would normally be, so the user can submit
                   straight from the keyboard without scrolling down to
                   find the "Generate invite link" button (which the
                   keyboard often covers on a phone). */
                enterKeyHint="send"
                autoFocus
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="btn-stack">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Generating link…' : 'Generate invite link'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setInviteOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div>
            {createdEmailSent ? (
              <div className="banner" style={{
                marginBottom: 12,
                padding: 10,
                background: '#E8F2EC',
                border: '1px solid #16A34A',
                borderRadius: 6,
                fontSize: 13,
              }}>
                ✓ We've emailed <strong>{createdEmail}</strong> the invitation. They should
                see it in a few seconds. The link below is a backup you can also share if needed.
              </div>
            ) : (
              <div className="banner" style={{
                marginBottom: 12,
                padding: 10,
                background: '#FFF8E5',
                border: '1px solid #BF8E3D',
                borderRadius: 6,
                fontSize: 13,
              }}>
                The auto-email didn't go through this time. No worries — send this link to{' '}
                <strong>{createdEmail}</strong> manually via WhatsApp, SMS or email:
              </div>
            )}
            <p style={{ margin: '0 0 8px' }}>
              Invitation link for <strong>{createdEmail}</strong>:
            </p>
            <div className="card" style={{ wordBreak: 'break-all', fontSize: 13, padding: 10, background: 'var(--bg, #FAF7F2)' }}>
              {createdLink}
            </div>
            <div className="btn-stack" style={{ marginTop: 12 }}>
              <button type="button" className="btn btn-primary" onClick={copyLink}>
                {copied ? '✓ Copied' : 'Copy link'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setInviteOpen(false)}>
                Done
              </button>
            </div>
            <p className="muted" style={{ fontSize: 11, marginTop: 12 }}>
              Send via WhatsApp, SMS or email. They tap it, sign up with{' '}
              <strong>{createdEmail}</strong>, and they're in. The link expires in 7 days.
            </p>
          </div>
        )}
      </Modal>
    </AppShell>
  );
}
